import { ref, computed, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { getClients } from "@/services/clientService";
import { getPackages } from "@/services/packageService";
import { getServices } from "@/services/serviceService";
import { getSidenavItems } from "@/services/sidenavService";
import type { PackageRecord } from "@/services/packageService";
import type { ServiceRecord } from "@/services/serviceService";
import type { SidenavItem } from "@/services/sidenavService";

export interface NormalizedClient {
  id: string | number;
  raw: any;
  companyName: string;
  referenceCode: string;
  initials: string;
  email: string;
  website: string;
  primaryService: string;
  displayServices: string[];
  servicePlanCode: string;
  servicePlanName: string;
  fullServices: Array<{
    salesId: string;
    serviceId: number | null;
    serviceName: string;
    status: string;
    dateOfSale: string;
  }>;
  serviceAccountTags: string[];
  statusLabel: string;
  statusTone: string;
  clientAccountStatus: string;
  clientAccountStatusTone: string;
  planLabel: string;
  packageFilterValue: string;
  planTone: string;
  accountStatus: string;
  websiteStatus: string;
  handlingAgent: string;
  assignedCcex: string;
  activationDateValue: string;
  activationDate: string;
  onboardingDateValue: string;
  onboardingDate: string;
  subscriptionStartDateValue: string;
  subscriptionStartDate: string;
  renewalDateValue: string;
  renewalDate: string;
  renewalStartDateValue: string;
  renewalStartDate: string;
  renewalEndDateValue: string;
  renewalEndDate: string;
  paymentDueDateValue: string;
  paymentDueDate: string;
  renewalStatus: string;
  renewalStatusTone: string;
  renewalCountdownDateValue: string;
  renewalCountdownDate: string;
  renewalCountdownDays: number | null;
  renewalCountdownLabel: string;
  renewalReminderLabel: string;
  renewalIsOverdue: boolean;
  dateAdded: string;
  sortDate: number | null;
  hasPassword: boolean;
  colorA: string;
  colorB: string;
}

const palette = [
  ["#eef4ff", "#b7cbff"],
  ["#fef3e8", "#f8bf8c"],
  ["#edf7f1", "#a9e0ba"],
  ["#f3efff", "#cab8ff"],
  ["#fff4d9", "#f7ce76"],
];

function normalizeServices(value: unknown) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") {
          return { name: item.trim(), plan: "" };
        }
        if (item && typeof item === "object") {
          return {
            name: String(
              (item as any).service_name || (item as any).name || ""
            ).trim(),
            plan: String((item as any).plan || "").trim(),
          };
        }
        return { name: "", plan: "" };
      })
      .filter((item) => item.name);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => ({ name: item.trim(), plan: "" }))
      .filter((item) => item.name);
  }
  return [];
}

function extractServiceNames(services: { name: string }[]) {
  return services.map((service) => service.name).filter(Boolean);
}

function normalizeImportedServices(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      return {
        salesId: String((item as any).sales_id || "").trim(),
        serviceId:
          typeof (item as any).service_id === "number"
            ? (item as any).service_id
            : Number.isFinite(Number((item as any).service_id))
            ? Number((item as any).service_id)
            : null,
        serviceName: String(
          (item as any).service_name || (item as any).name || ""
        ).trim(),
        status: String((item as any).plan || (item as any).status || "").trim(),
        dateOfSale: String((item as any).date_of_sale || "").trim(),
      };
    })
    .filter(
      (item): item is NonNullable<typeof item> => !!item && !!item.serviceName
    );
}

function extractServicePlans(services: { plan: string }[]) {
  return services
    .map((service) => String(service.plan || "").trim())
    .filter(Boolean);
}

function titleCase(value: unknown, fallback = "") {
  const source = String(value || fallback)
    .replace(/[_-]+/g, " ")
    .trim();
  if (!source) return fallback;
  return source.replace(/\w\S*/g, (word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

function normalizeAssignedCcex(value: unknown) {
  const normalized = String(value || "").trim();
  return normalized || "Unassigned";
}

function normalizeClientEmail(value: unknown) {
  const email = String(value || "").trim();

  if (!email) {
    return "No email provided";
  }

  if (
    email.toLowerCase().endsWith("@placeholder.local") ||
    email === "Please provide email address"
  ) {
    return "No email provided";
  }

  return email;
}

function deriveStatus(client: any) {
  const rawStatus = String(
    client.account_status ||
      client.status ||
      client.plan_status ||
      client.subscription_status ||
      ""
  )
    .trim()
    .toLowerCase();
  if (client.deleted_at) {
    return { label: "Inactive", tone: "inactive" };
  }
  if (["expired", "inactive", "disabled", "banned"].includes(rawStatus)) {
    return { label: "Inactive", tone: "inactive" };
  }
  if (["expiring", "trial", "pending"].includes(rawStatus)) {
    return { label: "Expiring", tone: "expiring" };
  }
  return { label: "Active", tone: "active" };
}

function deriveClientAccountStatus(client: any) {
  const rawStatus = String(client.client_account_status || "")
    .trim()
    .toLowerCase();
  const rawAccountStatus = String(client.account_status || "")
    .trim()
    .toLowerCase();

  const statuses = [rawStatus, rawAccountStatus].filter(Boolean);
  const lifecycleLabels: Record<string, { label: string; tone: string }> = {
    "for activation": { label: "For Activation", tone: "for-activation" },
    active: { label: "Active", tone: "active" },
    "on hold": { label: "On Hold", tone: "on-hold" },
    "grace period": { label: "Grace Period", tone: "grace-period" },
    offboarding: { label: "Offboarding", tone: "offboarding" },
    offboarded: { label: "Offboarded", tone: "offboarded" },
    "reactivation onboarding": {
      label: "Reactivation Onboarding",
      tone: "reactivation-onboarding",
    },
  };

  const explicitLifecycleStatus = rawStatus && lifecycleLabels[rawStatus];
  if (explicitLifecycleStatus) {
    return explicitLifecycleStatus;
  }

  const inactiveStatuses = [
    "inactive",
    "disabled",
    "expired",
    "banned",
    "archived",
    "cancelled",
    "canceled",
  ];
  const onHoldStatuses = [
    "on hold",
    "hold",
    "paused",
    "suspended",
  ];
  const activationStatuses = ["pending", "under review", "review", "trial"];

  if (statuses.includes("expired")) {
    return { label: "Offboarding", tone: "offboarding" };
  }

  if (
    client.deleted_at ||
    statuses.some((status) => inactiveStatuses.includes(status))
  ) {
    return { label: "Offboarded", tone: "offboarded" };
  }

  if (statuses.some((status) => activationStatuses.includes(status))) {
    return { label: "For Activation", tone: "for-activation" };
  }

  if (statuses.some((status) => onHoldStatuses.includes(status))) {
    return { label: "On Hold", tone: "on-hold" };
  }

  return { label: "Active", tone: "active" };
}

function derivePlan(client: any) {
  if (String(client.activation_plan_type || "").trim().toLowerCase() === "custom") {
    return { label: "Custom", tone: "starter" };
  }

  const assignedPackages = client.assigned_packages;
  if (Array.isArray(assignedPackages) && assignedPackages.length > 0) {
    const packageName = String(assignedPackages[0].name || "").trim();
    if (packageName) {
      return { label: packageName, tone: "starter" };
    }
  }
  const importedServices = normalizeImportedServices(client.services);
  const preferredService =
    importedServices.find(
      (service) => service.status.toLowerCase() === "active"
    ) || importedServices[0];

  if (preferredService?.serviceName) {
    return { label: preferredService.serviceName, tone: "starter" };
  }
  return { label: "—", tone: "starter" };
}

function buildReferenceCode(client: any, index: number) {
  const explicit = client.client_id || client.code || client.reference_code;
  if (explicit) return String(explicit);
  return `CL-${String(index + 1).padStart(3, "0")}`;
}

function getAssignedPackageName(client: any) {
  const assignedPackages = Array.isArray(client?.assigned_packages)
    ? client.assigned_packages
    : [];

  return String(assignedPackages[0]?.name || "").trim();
}

function normalizeServicePlanName(value: unknown) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function isDmpService(value: unknown) {
  const serviceName = normalizeServicePlanName(value);

  return (
    serviceName.startsWith("digital marketing partner") ||
    serviceName.includes("dmp upgrade seo") ||
    serviceName.includes("dmp upgrade smm")
  );
}

function isWebsiteLeadCaptureService(value: unknown) {
  return normalizeServicePlanName(value).includes("website lead capture");
}

function deriveServicePlan(client: any, serviceNames: string[]) {
  const assignedPackage = Array.isArray(client?.assigned_packages)
    ? client.assigned_packages[0]
    : null;
  const explicitPlan = client?.service_plan || assignedPackage?.service_plan;
  const explicitCode = String(explicitPlan?.code || "").trim();

  if (explicitCode) {
    return {
      code: explicitCode,
      name: String(explicitPlan?.name || explicitCode).trim(),
    };
  }

  const normalizedServices = serviceNames.map(normalizeServicePlanName);
  if (normalizedServices.some((name) => name.startsWith("ai powered micro app"))) {
    return { code: "ai_micro_app", name: "AI-Powered Micro App" };
  }
  if (normalizedServices.some(isDmpService)) {
    return { code: "dmp", name: "Digital Marketing Partner" };
  }
  if (normalizedServices.some(isWebsiteLeadCaptureService)) {
    return { code: "website_lead_capture", name: "Website Lead Capture" };
  }

  return { code: "", name: "Unassigned" };
}

function buildInitials(companyName: string) {
  const parts = String(companyName || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  const initials = parts.map((part) => part.charAt(0).toUpperCase()).join("");
  return initials || "CL";
}

function parseDateTimestamp(value: unknown) {
  const normalized = String(value || "").trim();
  if (!normalized) return null;

  const dateOnlyMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  const timestamp = new Date(normalized).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function isServiceForPlan(value: unknown, planCode: string) {
  const serviceName = normalizeServicePlanName(value);

  if (planCode === "ai_micro_app") {
    return serviceName.startsWith("ai powered micro app");
  }

  if (planCode === "dmp") {
    return isDmpService(serviceName);
  }

  if (planCode === "website_lead_capture") {
    return isWebsiteLeadCaptureService(serviceName);
  }

  return false;
}

export function getServicePlanSaleTimestamp(client: any) {
  const importedServices = normalizeImportedServices(client?.services);
  const saleTimestamps = importedServices
    .filter((service) =>
      ["dmp", "website_lead_capture", "ai_micro_app"].some((planCode) =>
        isServiceForPlan(service.serviceName, planCode)
      )
    )
    .map((service) => parseDateTimestamp(service.dateOfSale))
    .filter((timestamp): timestamp is number => timestamp !== null);

  return saleTimestamps.length > 0 ? Math.max(...saleTimestamps) : null;
}

function getDateAddedTimestamp(client: any) {
  const servicePlanSaleDate = getServicePlanSaleTimestamp(client);
  const sourceDateAdded = parseDateTimestamp(client.source_date_added);

  return (
    servicePlanSaleDate ??
    sourceDateAdded ??
    parseDateTimestamp(client.created_at)
  );
}

function getServiceAccountTags(
  fullServices: NormalizedClient["fullServices"]
) {
  const normalizedServiceNames = fullServices.map((service) =>
    normalizeServicePlanName(service.serviceName)
  );
  const tags: string[] = [];

  if (
    normalizedServiceNames.some((name) =>
      name.includes("dmp upgrade seo")
    )
  ) {
    tags.push("DMP - Upgrade (SEO)");
  }

  if (
    normalizedServiceNames.some((name) =>
      name.includes("dmp upgrade smm")
    )
  ) {
    tags.push("DMP - Upgrade (SMM)");
  }

  if (normalizedServiceNames.some(isWebsiteLeadCaptureService)) {
    tags.push("Website Lead Capture");
  }

  return tags;
}

function getClientSortTimestamp(client: any) {
  // Keep directory sorting anchored to the selected plan's sale date,
  // not to the manually editable activation date.
  return getDateAddedTimestamp(client);
}

function formatDateAdded(timestamp: number | null) {
  if (timestamp === null) {
    return "Unknown";
  }

  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateOnly(value: string) {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "Not scheduled";
  }

  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return "Not scheduled";
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function deriveRenewalStatusTone(status: string) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  if (normalized === "renewed") {
    return "renewed";
  }

  if (normalized === "expired" || normalized === "cancelled") {
    return "expired";
  }

  if (normalized === "paused") {
    return "paused";
  }

  if (normalized === "due soon") {
    return "due-soon";
  }

  if (normalized === "upcoming renewal") {
    return "upcoming";
  }

  return "active";
}

function normalizeRenewalStatusLabel(value: unknown, fallback = "Active") {
  const normalized = titleCase(value, fallback);
  return normalized.toLowerCase() === "active" ? "" : normalized;
}

function normalizeRenewalSummary(client: any) {
  const summary =
    client &&
    typeof client.renewal_summary === "object" &&
    client.renewal_summary
      ? client.renewal_summary
      : {};
  const renewalStatus = normalizeRenewalStatusLabel(
    summary.derived_status || client?.renewal_status,
    "Active"
  );
  const countdownDate = String(summary.countdown_date || "").trim();
  const renewalCountdownDays =
    typeof summary.days_until_renewal === "number"
      ? summary.days_until_renewal
      : null;
  const renewalIsOverdue =
    typeof summary.is_overdue === "boolean"
      ? summary.is_overdue
      : renewalCountdownDays != null
      ? renewalCountdownDays < 0
      : false;

  let renewalCountdownLabel = "Not scheduled";
  if (
    typeof summary.reminder_label === "string" &&
    summary.reminder_label.trim()
  ) {
    renewalCountdownLabel = summary.reminder_label.trim();
  } else if (renewalCountdownDays != null) {
    if (renewalCountdownDays === 0) {
      renewalCountdownLabel = "Due today";
    } else if (renewalCountdownDays > 0) {
      renewalCountdownLabel = `${renewalCountdownDays} days left`;
    } else {
      renewalCountdownLabel = `${Math.abs(renewalCountdownDays)} days overdue`;
    }
  }

  return {
    onboardingDateValue: String(
      summary.onboarding_date || client?.onboarding_date || ""
    ).trim(),
    subscriptionStartDateValue: String(
      summary.subscription_start_date || client?.subscription_start_date || ""
    ).trim(),
    renewalDateValue: String(
      summary.renewal_date || client?.renewal_date || ""
    ).trim(),
    renewalStartDateValue: String(
      summary.renewal_start_date || client?.renewal_start_date || ""
    ).trim(),
    renewalEndDateValue: String(
      summary.renewal_end_date || client?.renewal_end_date || ""
    ).trim(),
    paymentDueDateValue: String(
      summary.payment_due_date || client?.payment_due_date || ""
    ).trim(),
    renewalStatus,
    renewalStatusTone: deriveRenewalStatusTone(renewalStatus),
    renewalCountdownDateValue: countdownDate,
    renewalCountdownDate: formatDateOnly(countdownDate),
    renewalCountdownDays,
    renewalCountdownLabel,
    renewalReminderLabel: String(summary.reminder_label || "").trim(),
    renewalIsOverdue,
  };
}

function getStableClientSortId(client: NormalizedClient) {
  const id = Number(client.raw?.id ?? client.id);
  return Number.isFinite(id) ? id : 0;
}

function compareStableClientOrder(
  left: NormalizedClient,
  right: NormalizedClient,
  sortDirection: string
) {
  const leftId = getStableClientSortId(left);
  const rightId = getStableClientSortId(right);

  if (leftId !== rightId) {
    return sortDirection === "oldest" ? leftId - rightId : rightId - leftId;
  }

  return left.companyName.localeCompare(right.companyName);
}

export function useClientsQuery() {
  const searchQuery = ref("");
  const selectedPlan = ref("all");
  const selectedService = ref("all");
  const selectedHandlingAgent = ref("all");
  const selectedAssignedCcex = ref("all");
  const selectedActivationDate = ref("");
  const selectedSort = ref("newest");
  const currentPage = ref(1);
  const pageSize = ref(8);
  const viewMode = ref<"cards" | "list">("cards");

  const {
    data: clientsResponse,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["clients"],
    queryFn: getClients,
    refetchOnWindowFocus: false,
  });

  const { data: packagesResponse } = useQuery({
    queryKey: ["packages"],
    queryFn: () => getPackages(),
  });

  const { data: servicesResponse } = useQuery({
    queryKey: ["services"],
    queryFn: () => getServices(),
  });

  const { data: sidenavResponse } = useQuery({
    queryKey: ["sidenavItems"],
    queryFn: () => getSidenavItems(),
  });

  const rawClients = computed(() =>
    Array.isArray(clientsResponse.value?.data) ? clientsResponse.value.data : []
  );

  const packages = computed<PackageRecord[]>(() =>
    Array.isArray(packagesResponse.value?.data)
      ? packagesResponse.value.data
      : []
  );

  const servicesCatalog = computed<ServiceRecord[]>(() =>
    Array.isArray(servicesResponse.value?.data)
      ? servicesResponse.value.data
      : []
  );

  const sidenavItems = computed<SidenavItem[]>(() =>
    (sidenavResponse.value || []).filter((item: SidenavItem) => item.is_active)
  );

  const normalizedClients = computed<NormalizedClient[]>(() => {
    return (rawClients.value || []).map((client: any, index: number) => {
      const companyName =
        client.company_name || client.name || "Unnamed Client";
      const services = normalizeServices(client.services);
      const serviceNames = extractServiceNames(services);
      const fullServices = normalizeImportedServices(client.services);
      const serviceAccountTags = getServiceAccountTags(fullServices);
      const servicePlan = deriveServicePlan(client, serviceNames);
      const status = deriveStatus(client);
      const clientAccountStatus = deriveClientAccountStatus(client);
      const plan = derivePlan(client);
      const assignedPackageName = getAssignedPackageName(client);
      const paletteEntry = palette[index % palette.length];
      const dateAddedTimestamp = getDateAddedTimestamp(client);
      const clientSortTimestamp = getClientSortTimestamp(client);
      const renewal = normalizeRenewalSummary(client);

      return {
        id: client.id ?? client.client_id ?? index,
        raw: client,
        companyName,
        referenceCode: buildReferenceCode(client, index),
        initials: buildInitials(companyName),
        email: normalizeClientEmail(client.email),
        website: client.website_url || client.url || "No website provided",
        primaryService: serviceNames[0] || "General Support",
        displayServices: serviceNames.length
          ? serviceNames.slice(0, 2)
          : ["General Support"],
        servicePlanCode: servicePlan.code,
        servicePlanName: servicePlan.name,
        fullServices,
        serviceAccountTags,
        statusLabel: status.label,
        statusTone: status.tone,
        clientAccountStatus: clientAccountStatus.label,
        clientAccountStatusTone: clientAccountStatus.tone,
        planLabel: plan.label,
        packageFilterValue: assignedPackageName,
        planTone: plan.tone,
        accountStatus: titleCase(client.account_status, "Unknown"),
        websiteStatus: titleCase(client.website_status, "Unknown"),
        handlingAgent: client.handling_agent || "Unassigned",
        assignedCcex: normalizeAssignedCcex(client.assigned_ccex),
        activationDateValue: client.activation_date || "",
        activationDate: formatDateOnly(client.activation_date || ""),
        onboardingDateValue: renewal.onboardingDateValue,
        onboardingDate: formatDateOnly(renewal.onboardingDateValue),
        subscriptionStartDateValue: renewal.subscriptionStartDateValue,
        subscriptionStartDate: formatDateOnly(
          renewal.subscriptionStartDateValue
        ),
        renewalDateValue: renewal.renewalDateValue,
        renewalDate: formatDateOnly(renewal.renewalDateValue),
        renewalStartDateValue: renewal.renewalStartDateValue,
        renewalStartDate: formatDateOnly(renewal.renewalStartDateValue),
        renewalEndDateValue: renewal.renewalEndDateValue,
        renewalEndDate: formatDateOnly(renewal.renewalEndDateValue),
        paymentDueDateValue: renewal.paymentDueDateValue,
        paymentDueDate: formatDateOnly(renewal.paymentDueDateValue),
        renewalStatus: renewal.renewalStatus,
        renewalStatusTone: renewal.renewalStatusTone,
        renewalCountdownDateValue: renewal.renewalCountdownDateValue,
        renewalCountdownDate: renewal.renewalCountdownDate,
        renewalCountdownDays: renewal.renewalCountdownDays,
        renewalCountdownLabel: renewal.renewalCountdownLabel,
        renewalReminderLabel: renewal.renewalReminderLabel,
        renewalIsOverdue: renewal.renewalIsOverdue,
        dateAdded: formatDateAdded(dateAddedTimestamp),
        sortDate: clientSortTimestamp,
        hasPassword: !!client.has_password,
        colorA: paletteEntry[0],
        colorB: paletteEntry[1],
      };
    });
  });

  const availablePlans = computed(() => {
    return [
      ...new Set(
        packages.value
          .filter((pkg) => String(pkg?.status || "").toLowerCase() === "active")
          .map((pkg) => String(pkg.name || "").trim())
          .filter(Boolean)
      ),
    ];
  });

  const planOptions = computed(() => {
    return [
      { label: "All Plans", value: "all" },
      ...availablePlans.value.map((plan) => ({ label: plan, value: plan })),
    ];
  });

  const serviceOptions = computed(() => {
    return [
      { label: "All AccountService Plans", value: "all" },
      {
        label: "Digital Marketing Partner",
        value: "dmp",
      },
      {
        label: "AI-Powered Micro App",
        value: "ai_micro_app",
      },
      {
        label: "Website Lead Capture",
        value: "website_lead_capture",
      },
    ];
  });

  const assignedCcexOptions = computed(() => {
    const options = [
      ...new Set(normalizedClients.value.map((client) => client.assignedCcex)),
    ]
      .filter(Boolean)
      .sort((left, right) => {
        if (left === "Unassigned") return 1;
        if (right === "Unassigned") return -1;
        return left.localeCompare(right);
      });

    return [
      { label: "All Agents", value: "all" },
      ...options.map((assignedCcex) => ({
        label: assignedCcex,
        value: assignedCcex,
      })),
    ];
  });

  const handlingAgentOptions = computed(() => {
    const options = [
      ...new Set(normalizedClients.value.map((client) => client.handlingAgent)),
    ]
      .filter(Boolean)
      .sort((left, right) => {
        if (left === "Unassigned") return 1;
        if (right === "Unassigned") return -1;
        return left.localeCompare(right);
      });

    return [
      { label: "All Agents", value: "all" },
      ...options.map((handlingAgent) => ({
        label: handlingAgent,
        value: handlingAgent,
      })),
    ];
  });

  const sortOptions = computed(() => {
    return [
      { label: "Latest", value: "newest" },
      { label: "Oldest", value: "oldest" },
    ];
  });

  const filteredClients = computed(() => {
    const query = searchQuery.value.toLowerCase();
    const filtered = normalizedClients.value.filter((client) => {
      const matchesPlan =
        selectedPlan.value === "all" ||
        client.packageFilterValue === selectedPlan.value;
      const matchesActivationDate =
        !selectedActivationDate.value ||
        client.activationDateValue === selectedActivationDate.value;
      const matchesService =
        selectedService.value === "all" ||
        client.servicePlanCode === selectedService.value ||
        client.fullServices.some((service) =>
          isServiceForPlan(service.serviceName, selectedService.value)
        );
      const matchesAssignedCcex =
        selectedAssignedCcex.value === "all" ||
        client.assignedCcex === selectedAssignedCcex.value;
      const matchesHandlingAgent =
        selectedHandlingAgent.value === "all" ||
        client.handlingAgent === selectedHandlingAgent.value;
      const matchesSearch =
        !query ||
        client.companyName.toLowerCase().includes(query) ||
        client.referenceCode.toLowerCase().includes(query) ||
        client.email.toLowerCase().includes(query);
      return (
        matchesPlan &&
        matchesActivationDate &&
        matchesService &&
        matchesAssignedCcex &&
        matchesHandlingAgent &&
        matchesSearch
      );
    });

    return [...filtered].sort((left, right) => {
      const leftDate = left.sortDate;
      const rightDate = right.sortDate;

      if (leftDate === null && rightDate === null) {
        return compareStableClientOrder(left, right, selectedSort.value);
      }

      if (leftDate === null) {
        return 1;
      }

      if (rightDate === null) {
        return -1;
      }

      if (leftDate === rightDate) {
        return compareStableClientOrder(left, right, selectedSort.value);
      }

      return selectedSort.value === "oldest"
        ? leftDate - rightDate
        : rightDate - leftDate;
    });
  });

  const totalPages = computed(() => {
    return Math.max(
      1,
      Math.ceil(filteredClients.value.length / pageSize.value)
    );
  });

  const paginatedClients = computed(() => {
    const start = (currentPage.value - 1) * pageSize.value;
    return filteredClients.value.slice(start, start + pageSize.value);
  });

  const paginationStart = computed(() => {
    if (!filteredClients.value.length) return 0;
    return (currentPage.value - 1) * pageSize.value + 1;
  });

  const paginationEnd = computed(() => {
    return Math.min(
      currentPage.value * pageSize.value,
      filteredClients.value.length
    );
  });

  const generatedClientId = computed(() => {
    const highestSuffix = (rawClients.value || []).reduce(
      (max: number, client: any) => {
        const match = String(client?.client_id || "").match(/^CL-(\d+)$/);
        if (!match) return max;
        return Math.max(max, Number(match[1]));
      },
      1000
    );
    return `CL-${String(highestSuffix + 1).padStart(4, "0")}`;
  });

  watch(
    [
      searchQuery,
      selectedPlan,
      selectedService,
      selectedHandlingAgent,
      selectedAssignedCcex,
      selectedActivationDate,
      selectedSort,
    ],
    () => {
      currentPage.value = 1;
    }
  );

  watch(filteredClients, () => {
    if (currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value;
    }
  });

  return {
    searchQuery,
    selectedPlan,
    selectedService,
    selectedHandlingAgent,
    selectedAssignedCcex,
    selectedActivationDate,
    selectedSort,
    currentPage,
    pageSize,
    viewMode,
    clients: rawClients,
    normalizedClients,
    filteredClients,
    paginatedClients,
    packages,
    servicesCatalog,
    sidenavItems,
    planOptions,
    serviceOptions,
    handlingAgentOptions,
    assignedCcexOptions,
    sortOptions,
    totalPages,
    paginationStart,
    paginationEnd,
    isLoading,
    isFetching,
    isError,
    error,
    generatedClientId,
  };
}
